import type { UserSearchInput } from '@generated/graphql';
import { generateClientNameString } from '@utils/generateClientNameString';
import {
  RECON_FLAGS_MAX_ACCOUNT_IDS,
  RECON_FLAGS_MAX_USER_SEARCH_RESULTS,
} from './reconFlags.config';

/** Client option shown in the recon flags Autocomplete (`searchUsers`). */
export type ReconFlagsClientOption = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  entityName?: string | null;
  primaryEmail?: string | null;
  accounts: readonly { id: string }[];
};

/** Raw `searchUsers` hit before accounts are normalized to `[]`. */
type ReconFlagsUserSearchHit = Omit<ReconFlagsClientOption, 'accounts'> & {
  accounts?: readonly { id: string }[] | null;
};

/** Variables for `searchUsersForReconFlagsGrid`. */
export function buildSearchUsersForReconFlagsVariables(
  orgId: string,
  query: string,
): { input: UserSearchInput } {
  return {
    input: {
      filter: {
        query,
        organizationId: orgId,
        restrictToOrganizationId: false,
      },
      pagination: {
        perPage: RECON_FLAGS_MAX_USER_SEARCH_RESULTS,
      },
    },
  };
}

/** Display label for a client option (entity/person name, else email, else id). */
export function formatReconFlagsClientOptionLabel(
  client: ReconFlagsClientOption,
): string {
  const name = generateClientNameString(client, false, true).trim();
  if (name && name !== client.id) return name;
  const email = client.primaryEmail?.trim();
  if (email) return email;
  return name || client.id;
}

/** Maps `searchUsers` hits into Autocomplete options. */
export function mapReconFlagsUserSearchHits(
  users: readonly ReconFlagsUserSearchHit[] | null | undefined,
): ReconFlagsClientOption[] {
  return (users ?? [])
    .slice(0, RECON_FLAGS_MAX_USER_SEARCH_RESULTS)
    .map((user) => ({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      entityName: user.entityName,
      primaryEmail: user.primaryEmail,
      accounts: (user.accounts ?? []).map((account) => ({ id: account.id })),
    }));
}

/**
 * `accountIds` for `fetchFlagsForGrid` from the selected client.
 * - `undefined` → no client selected (omit filter)
 * - `[]` → client selected but has no accounts (empty grid)
 * - non-empty → filter flags to those accounts (capped)
 */
export function buildReconFlagsAccountIdsFromClientSelection(
  selectedClient: ReconFlagsClientOption | null,
): string[] | undefined {
  if (!selectedClient) {
    return undefined;
  }
  return selectedClient.accounts
    .map((account) => account.id)
    .filter(Boolean)
    .slice(0, RECON_FLAGS_MAX_ACCOUNT_IDS);
}
